import {cloneDeep} from '@shared/helpers/lang'
import {mapValueKeys} from '@shared/helpers/object'

import {OrderSheetCouponState, toggleUseAllCoupon} from '@web/slices/orderSheetCoupon/slice'
import makeSlice from '@web/slices/orderSheetCoupon/sliceMaker'

import {fetchDiscountsReload, fetchSubscription, restoreSubscription} from '../../actions'

import {
    convertDeliveryFeeCoupon,
    convertImmediateCoupon,
    convertProductCoupon,
    convertStoreCoupon,
    getDiscountInfo,
    getProductCouponType,
} from './converter'

const slice = makeSlice({
    extraReducers: (builder) => {
        builder
            .addCase(fetchSubscription.fulfilled, (state, {payload: {subscriptionViewResult}}) => {
                const {coupon} = subscriptionViewResult

                /**
                 * 나할과제부터 모든쿠폰을다 먹여도 음수가 되는 케이스는 없음
                 * - 필수적용쿠폰(즉시할인, 정기구독할인)만으로 음수가 된다면 신청서 진입불가
                 */
                state.useAllCoupon = true
                state.canUseAllCoupon = true

                const selectedCoupon: OrderSheetCouponState['selected'] = {
                    /** 즉시할인 */
                    immediateCoupon: convertImmediateCoupon(coupon.selectedImmediateDiscountListsByOrderSheetItemId),
                    /** 상품할인 */
                    productCoupon: convertProductCoupon(coupon.selectedProductCouponListsByOrderSheetItemId),
                    /** 상품 중복 할인 */
                    duplicateCoupon: mapValueKeys((couponItem) => {
                        if (couponItem) {
                            return [
                                {
                                    ...couponItem,
                                    couponType: getProductCouponType({
                                        discountType: couponItem.couponDiscountType,
                                        providerType: couponItem.couponProviderType,
                                    }),
                                    disabled: false,
                                    exclusiveCouponType: couponItem.exclusiveCouponType ?? undefined,
                                },
                            ]
                        }
                        return []
                    }, coupon.selectedProductDuplicateCouponsByOrderSheetItemId),

                    /** 스토어 할인 */
                    storeCoupon: mapValueKeys(convertStoreCoupon, coupon.selectedStoreCouponsByMerchantNo),

                    /** 배송비 할인 */
                    deliveryCoupon: mapValueKeys(
                        convertDeliveryFeeCoupon,
                        coupon.selectedDeliveryFeeCouponsByDeliveryGroupKey,
                    ),

                    /** 복수구매 할인 */
                    multiPurchaseCoupon: coupon.multiPurchaseDiscountSelectedOrderSheetItemIds,

                    /** 정기구독 할인 */
                    subscriptionCoupon: coupon.subscriptionDiscountSelectedOrderSheetItemIds,
                }

                state.selected = selectedCoupon
                /** 최초 선택되어 내려오는 쿠폰 목록은 최적화된 쿠폰 목록 */
                state.optimizedCoupon = selectedCoupon

                // 할인 금액 정보
                state.discount = getDiscountInfo(coupon)

                state.subscriptionDiscountPolicesByOrderSheetItemId =
                    subscriptionViewResult.subscription.subscriptionDiscountListsByOrderSheetItemId
            })
            .addCase(fetchDiscountsReload.fulfilled, (state, {payload}) => {
                const {coupon} = payload

                const selectedCoupon: OrderSheetCouponState['selected'] = {
                    immediateCoupon: convertImmediateCoupon(coupon.selectedImmediateDiscountListsByOrderSheetItemId),
                    productCoupon: convertProductCoupon(coupon.selectedProductCouponListsByOrderSheetItemId),
                    duplicateCoupon: mapValueKeys((couponItem) => {
                        if (couponItem) {
                            return [
                                {
                                    ...couponItem,
                                    couponType: getProductCouponType({
                                        discountType: couponItem.couponDiscountType,
                                        providerType: couponItem.couponProviderType,
                                    }),
                                    disabled: false,
                                    exclusiveCouponType: couponItem.exclusiveCouponType ?? undefined,
                                },
                            ]
                        }
                        return []
                    }, coupon.selectedProductDuplicateCouponsByOrderSheetItemId),
                    storeCoupon: mapValueKeys(convertStoreCoupon, coupon.selectedStoreCouponsByMerchantNo),
                    deliveryCoupon: mapValueKeys(
                        convertDeliveryFeeCoupon,
                        coupon.selectedDeliveryFeeCouponsByDeliveryGroupKey,
                    ),
                    multiPurchaseCoupon: coupon.multiPurchaseDiscountSelectedOrderSheetItemIds,
                    subscriptionCoupon: coupon.subscriptionDiscountSelectedOrderSheetItemIds,
                }
                state.selected = selectedCoupon
                state.optimizedCoupon = selectedCoupon
                state.discount = getDiscountInfo(coupon)
            })
            .addCase(toggleUseAllCoupon, (state) => {
                /** 결제시 모두 사용을 켰으면 일회성 쿠폰을 optimized coupon으로 덮어 씌운다 */
                const shouldOptimize = !state.useAllCoupon

                state.selected.productCoupon = shouldOptimize ? cloneDeep(state.optimizedCoupon.productCoupon) : {}
                state.selected.duplicateCoupon = shouldOptimize ? cloneDeep(state.optimizedCoupon.duplicateCoupon) : {}
                state.selected.storeCoupon = shouldOptimize ? cloneDeep(state.optimizedCoupon.storeCoupon) : {}

                state.useAllCoupon = !state.useAllCoupon
            })
            .addCase(restoreSubscription.fulfilled, (state, {payload: {storedState}}) => {
                const useAllCoupon = storedState.orderSheetCoupon.useAllCoupon

                state.useAllCoupon = useAllCoupon

                /** 복원했는데 쿠폰 모두사용이 false라면 일시적 쿠폰을 모두 미선택한 것으로 반영한다. */
                if (!useAllCoupon) {
                    state.selected.productCoupon = {}
                    state.selected.duplicateCoupon = {}
                    state.selected.storeCoupon = {}
                }
            })
    },
})

export default slice.reducer
export const actions = slice.actions
